/**
 * Select — primitive de sélection, calquée sur {@link TextInput}.
 *
 * @remarks
 * Le design system exposait `TextInput`, `Textarea`, `Checkbox`, `RadioGroup`,
 * `Toggle`, `Slider`… mais **pas de select**. Chacune des 23 vues qui en avait
 * besoin a donc résolu le problème dans son coin : 8 variantes visuelles du
 * même contrôle, trois fonds différents (`surface`, `bg-hover`,
 * `surface-raised`), et un chevron redessiné dans 5 feuilles seulement — les
 * 9 autres gardant le rendu natif de l'OS (SAAS-910).
 *
 * ### Le chevron n'est pas une image
 *
 * Les 5 feuilles qui le redessinaient le faisaient en SVG data-URI, avec la
 * couleur de trait **écrite en dur** dans l'URL :
 *
 * ```css
 * background-image: url("data:image/svg+xml,…stroke='%239ca3af'…");
 * ```
 *
 * Un gris fixe, donc : le chevron ne suivait aucun thème. Et ni le scanner
 * anti-hardcoding (qui ne lit que les `.ts`/`.tsx`) ni stylelint `color-no-hex`
 * (qui analyse des valeurs de couleur, pas du texte dans `url()`, encore moins
 * encodé `%23`) ne pouvaient le voir.
 *
 * Ici c'est un `<ChevronDown>` en `currentColor` sur un token. Il n'y a plus de
 * couleur à figer — donc plus rien à oublier de mettre à jour.
 *
 * ### `size` est réservé
 *
 * L'attribut natif `size` d'un `<select>` transforme le contrôle en listbox à
 * N lignes visibles. Aucune vue ne l'utilise, et ce n'est pas un rendu que le
 * DS veut proposer. Le nom est donc repris pour l'échelle du composant, comme
 * sur `Button` — au prix d'un `Omit`, assumé pour garder une API homogène.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

type SelectSize = 'sm' | 'md';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  error?: boolean;
  /** `md` s'aligne sur `TextInput` (h-9), `sm` sur les barres de filtres (h-8). */
  size?: SelectSize;
  /**
   * Classes du conteneur, pas du `<select>`.
   *
   * Le chevron est positionné par rapport au conteneur : c'est donc lui qui
   * porte la largeur quand elle n'est pas `w-full` (`w-16`, `max-w-52`…).
   */
  containerClassName?: string;
}

const SIZES: Record<SelectSize, string> = {
  sm: 'h-8 pr-7 pl-2 text-xs',
  md: 'h-9 pr-8 pl-3 text-sm',
};

const CHEVRON_SIZES: Record<SelectSize, string> = {
  sm: 'right-2 size-3',
  md: 'right-3 size-3.5',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, containerClassName, error = false, size = 'md', ...rest },
  ref,
) {
  return (
    <div className={cn('relative w-full', containerClassName)}>
      <select
        ref={ref}
        className={cn(
          'bg-surface w-full cursor-pointer appearance-none rounded-md border transition-colors',
          'focus-visible:outline-primary-500 focus-visible:outline-2 focus-visible:outline-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error ? 'border-error' : 'border-border-strong hover:border-border',
          SIZES[size],
          className,
        )}
        {...rest}
      />
      {/* `pointer-events-none` : le chevron est décoratif, le clic doit
          atteindre le select en dessous — c'est lui qui ouvre la liste. */}
      <ChevronDown
        aria-hidden
        className={cn(
          'text-fg-muted pointer-events-none absolute top-1/2 -translate-y-1/2',
          rest.disabled === true && 'opacity-50',
          CHEVRON_SIZES[size],
        )}
      />
    </div>
  );
});
